import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Package, ClipboardList, Users, TrendingUp, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AdminDeals from "@/pages/AdminDeals";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/bookings").then(r => setBookings(r.data.bookings));
    api.get("/packages").then(r => setPackages(r.data.packages));
    api.get("/contacts").then(r => setContacts(r.data.contacts));
  }, [user]);

  if (loading) return <div className="pt-32 text-center text-slate-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace/>;

  const delPkg = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    await api.delete(`/packages/${slug}`);
    setPackages(packages.filter(p => p.slug !== slug));
    toast.success("Deleted");
  };

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">Admin</div>
          <h1 className="mt-3 text-4xl font-bold text-blue-950 dark:text-white">Dashboard</h1>
        </div>
        <div className="flex gap-2 mb-8 flex-wrap">
          {[["dashboard","Overview"],["bookings","Bookings"],["packages","Packages"],["deals","Deals"],["contacts","Enquiries"]].map(([k,l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={()=>setTab(k)}
              className={`px-5 py-2 rounded-full text-sm font-semibold ${tab===k?"btn-gold":"glass"}`}>{l}</button>
          ))}
        </div>

        {tab === "dashboard" && stats && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card icon={ClipboardList} label="Total Bookings" val={stats.total_bookings}/>
            <Card icon={TrendingUp} label="Confirmed" val={stats.confirmed_bookings}/>
            <Card icon={Users} label="Customers" val={stats.total_customers}/>
            <Card icon={Package} label="Packages" val={stats.total_packages}/>
            <div className="md:col-span-2 lg:col-span-4 rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
              <div className="text-xs uppercase tracking-widest text-slate-500">Total Revenue</div>
              <div className="mt-2 text-5xl font-bold text-blue-950 dark:text-white">₹{stats.revenue.toLocaleString('en-IN')}</div>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-white/5 text-left"><tr>
                <th className="p-4">Traveller</th><th className="p-4">Package</th><th className="p-4">Date</th><th className="p-4">Pax</th><th className="p-4">Amount</th><th className="p-4">Status</th>
              </tr></thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} className="border-t border-slate-100 dark:border-white/5">
                    <td className="p-4"><div className="font-medium">{b.traveller_name}</div><div className="text-xs text-slate-500">{b.email} · {b.phone}</div></td>
                    <td className="p-4">{b.package_title}</td>
                    <td className="p-4">{b.travel_date}</td>
                    <td className="p-4">{b.passengers}</td>
                    <td className="p-4">₹{b.amount.toLocaleString('en-IN')}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${b.status === "confirmed" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "packages" && (
          <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-white/5 text-left"><tr>
                <th className="p-4">Package</th><th className="p-4">Location</th><th className="p-4">Price</th><th className="p-4">Category</th><th className="p-4"></th>
              </tr></thead>
              <tbody>
                {packages.map(p => (
                  <tr key={p.slug} className="border-t border-slate-100 dark:border-white/5">
                    <td className="p-4 font-medium">{p.title}</td>
                    <td className="p-4">{p.location}</td>
                    <td className="p-4">₹{p.price.toLocaleString('en-IN')}</td>
                    <td className="p-4">{p.category}</td>
                    <td className="p-4"><button data-testid={`admin-delete-${p.slug}`} onClick={()=>delPkg(p.slug)} className="text-red-500 hover:underline flex items-center gap-1"><Trash2 className="w-3.5 h-3.5"/> Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "deals" && <AdminDeals />}


        {tab === "contacts" && (
          <div className="grid md:grid-cols-2 gap-4">
            {contacts.map(c => (
              <div key={c.id} className="rounded-3xl p-6 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
                <div className="flex justify-between"><div className="font-semibold text-blue-950 dark:text-white">{c.name}</div><div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</div></div>
                <div className="text-xs text-slate-500">{c.email} · {c.phone}</div>
                <div className="mt-2 text-sm font-semibold text-[#D4AF37]">{c.subject}</div>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{c.message}</p>
              </div>
            ))}
            {!contacts.length && <div className="text-slate-500">No enquiries yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
function Card({ icon: Icon, label, val }) {
  return (
    <div className="rounded-3xl p-6 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 card-lift">
      <Icon className="w-6 h-6 text-[#D4AF37]"/>
      <div className="mt-4 text-3xl font-bold text-blue-950 dark:text-white">{val}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
